import React from "react";
import { useState } from "react";
import { X, FolderPlus, Loader2 } from "lucide-react";
import { validateFolderName } from "../../utils/utilFunctions";

interface FolderData {
  name: string;
  description: string;
}

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderData: FolderData;
  setFolderData: (data: FolderData) => void;
  onSubmit: () => void;
  isLoading: boolean;
  isEditing: boolean;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  isOpen,
  onClose,
  folderData,
  setFolderData,
  onSubmit,
  isLoading,
  isEditing,
}) => {
  if (!isOpen) return null;
  const [error, setError] = useState("");
  const [descError, setDescError] = useState("");
  const handleSubmit = () => {
    if (folderData.name.trim()) {
      onSubmit();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-md animate-in zoom-in-95 duration-200">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Modal Content */}
          <div className="pt-12 p-6">
            <div className="flex items-center justify-between pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FolderPlus className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isEditing ? "Edit Folder" : "Create New Folder"}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={folderData.name}
                  onChange={(e) => {
                   const value = e.target.value;

                   if (!validateFolderName(value)) {
                    setError(
                     "Only alphabets, numbers, and spaces are allowed"
                    );
                    return;
                   }

                   setError("");

                   setFolderData({
                    ...folderData,
                    name: value,
                   });
                   }}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
                  placeholder="Enter folder name"
                  autoFocus
                  maxLength={100}
                  onBlur={() => setError("")}
                />
                {
                  error && (
                    <p className="text-red-500 text-sm mt-1">
                      {error}
                    </p>
                  )
                }
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={folderData.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^[A-Za-z0-9\s_.,-]*$/.test(value)) {
                      setDescError("Only alphabets, numbers, spaces, hyphens, underscores, commas and periods are allowed");
                      return;
                    }
                    setDescError("");
                    setFolderData({
                      ...folderData,
                      description: value,
                    });
                  }}
                  onBlur={() => setDescError("")}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all resize-none"
                  placeholder="Enter folder description"
                  rows={3}
                />
                {descError && (
                  <p className="text-red-500 text-sm mt-1">{descError}</p>
                )}
              </div>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!folderData.name.trim() || isLoading}
                className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-2"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEditing ? "Update Folder" : "Create Folder"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateFolderModal;
